<template>
  <div class="max-w-3xl" v-if="page">
    <div class="adthrive-header min-h-[280px] overflow-hidden"></div>
    <Ad mb-4 ad-slot="6966721074" />
    <h1 class="font-slab text-2xl text-brand-blue">{{ page.title }}</h1>
    <div space-y-10 mt-4>
      <LocationProblem v-if="locationError" :error="locationError" :subject="locationSubject" />
      <div v-else>
        <template v-if="loading || !fieldValue">
          <p h-12 w-40 bg-stone-100 rounded animate-pulse :class="{ invisible: !loading }"></p>
        </template>
        <template v-else>
          <slot :page="page" :data="data">
            <p class="font-bold whitespace-pre-line" :class="page.fields?.valueClass || 'text-5xl'">{{ fieldValue }}</p>
          </slot>
        </template>
      </div>
      <LocationLookup v-model="selectedLocation" />
      <div space-y-4>
        <Ad ad-slot="1948746426" />
        <h2 text-2xl font-semibold text-stone-500>{{ page.label }} Map</h2>
        <LazyMapAddress
          :location="coordinates"
          :field="page.meta.field"
          :value="fieldValue"
          @markerMove="setMapLocation"
        />
        <Ad ad-slot="3658187561" />
        <h2 text-2xl font-semibold text-stone-500>More Information</h2>
        <div grid grid-flow-dense sm:grid-cols-2 gap-2>
          <div v-for="item in ADDRESS_FIELDS" contents>
            <DataItem
              v-if="
                page.meta.field !== item.field && !['nativeLands', 'countySubdivision.fullName'].includes(item.field)
              "
              :loading="loading"
              :label="isInternational && item?.internationalLabel ? item?.internationalLabel : item.label"
              :value="getDataValue(item.field)"
              valueClass="text-xl sm:text-2xl"
              col-span-2
              sm:col-span-1
              :class="item.class"
            />
            <CountySubdivisionDataItem
              v-else-if="item.field == 'countySubdivision.fullName'"
              :loading="loading"
              :data="data.countySubdivision"
            />
            <DataItem
              v-else-if="item.field == 'nativeLands'"
              :loading="loading"
              :label="item.label"
              :value="data.nativeLands?.length > 0 ? 'show' : null"
              :copyOnClick="false"
              valueClass="text-lg sm:text-xl"
              col-span-2
              :class="{ 'sm:col-span-1': data.nativeLands?.length < 2 }"
            >
              <NativeLands :nativeLands="data.nativeLands" />
            </DataItem>
          </div>
        </div>
      </div>
      <div space-y-4 v-if="address && selectedLocation?.module === 'currentLocation'">
        <!-- <h2 text-2xl font-semibold text-stone-500>Settings</h2>
        <div space-x-lg>
          <Button
            color="blue"
            :icon="
              watchMode ? 'i-material-symbols-pause-rounded text-lg' : 'i-material-symbols-play-arrow-rounded text-lg'
            "
            @click="watchMode = !watchMode"
          >
            {{ watchMode ? "Pause updates" : "Live update" }}
          </Button>
        </div> -->
        <div text-stone-5 prose lg:text-lg v-if="isLocationSupported">
          <p v-if="!loading && address?.error && !isBot()">An error occurred. Please try again later.</p>
          <p v-else-if="!loading && !getDataValue(page.meta.field) && !isBot()">
            We could not determine the {{ page.label }} for your location.
          </p>
          <p>
            By default, the site will gather the gps coordinates directly from your device and then uses that
            information to lookup information about where you are.
          </p>
        </div>
      </div>
      <div class="prose lg:text-lg">
        <h2 text-3xl font-semibold text-stone-500>{{ page?.subtitle || "What is this?" }}</h2>

        <ContentRenderer v-if="page" :value="page" />

        <WikipediaInfo v-if="placeName" :slug="placeName" />
        <WikipediaInfo v-if="page.wiki" :slug="page.wiki" />

        <h3 text-2xl font-semibold text-stone-500>More Resources</h3>
        <div flex flex-wrap gap-2>
          <NuxtLink to="/what-county-am-i-in" hover:text-black>What county am I in?</NuxtLink>
          <NuxtLink to="/what-is-my-parish" hover:text-black>What parish am I in?</NuxtLink>
          <NuxtLink to="/what-is-my-state" hover:text-black>What state am I in?</NuxtLink>

          <NuxtLink to="/what-township-am-i-in" hover:text-black>What township am I in?</NuxtLink>
          <NuxtLink to="/what-town-am-i-in" hover:text-black>What town am I in?</NuxtLink>
          <NuxtLink to="/what-municipality-am-i-in" hover:text-black>What municipality am I in?</NuxtLink>
          <NuxtLink to="/what-village-am-i-in" hover:text-black>What village am I in?</NuxtLink>
          <NuxtLink to="/what-district-am-i-in" hover:text-black>What district am I in?</NuxtLink>
          <NuxtLink to="/what-barrio-am-i-in" hover:text-black>What barrio am I in?</NuxtLink>

          <NuxtLink to="/what-school-district-am-i-in" hover:text-black>What school district am I in?</NuxtLink>
          <NuxtLink to="/what-neighborhood-am-i-in" hover:text-black>What neighborhood am I in?</NuxtLink>
          <NuxtLink to="/what-is-my-zipcode" hover:text-black>What ZIP code am I in?</NuxtLink>
          <NuxtLink to="/what-is-my-city" hover:text-black>What city am I in?</NuxtLink>
          <NuxtLink to="/what-zone-am-i-in" hover:text-black>What plant hardiness zone am I in?</NuxtLink>
          <!-- <NuxtLink to="/what-indigenous-territories-am-i-in" hover:text-black
            >What indigenous territories am I in?</NuxtLink
          > -->
          <NuxtLink to="/what-is-my-country" hover:text-black>What country am I in?</NuxtLink>
          <NuxtLink to="/what-is-my-intersection" hover:text-black>What is my intersection?</NuxtLink>
          <NuxtLink to="/what-are-my-gps-coordinates" hover:text-black>Current GPS coordinates</NuxtLink>
          <NuxtLink to="/county-map" hover:text-black>County maps</NuxtLink>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
const props = defineProps({
  path: {
    type: String,
    default: "address",
  },
});
const { reverseGeocode } = useMapkit();
const {
  selectedLocation,
  coordinates,
  placeName,
  locationError,
  setMapLocation,
  useInitialLocationData,
  onCoordinatesChange,
  geolocation,
} = useSelectedLocation();
const { startWatching, stopWatching } = geolocation;

const address = ref();
const countySubdivision = ref();
const zone = ref();
const nativeLands = ref();
const loading = ref(true);
const watchMode = ref(false);

const isInternational = computed(() => {
  if (!data.value?.countryRegion) {
    return false;
  }
  return data.value.countryRegion != "United States";
});

const data = computed(() => {
  return {
    ...address.value,
    countySubdivision: countySubdivision.value,
    zone: zone.value,
    nativeLands: nativeLands.value,
  };
});

function getDataValue(field) {
  const path = field?.split(".");
  if (!path) {
    return null;
  }
  let value = data.value;
  for (const key of path) {
    value = value?.[key];
  }
  return value;
}

async function update() {
  loading.value = true;
  address.value = null;
  countySubdivision.value = null;
  zone.value = null;
  nativeLands.value = null;
  try {
    const requests = [
      reverseGeocode(coordinates.value),
      useFetchCountySubdivision(coordinates.value),
      useFetchZone(coordinates.value),
      //   useFetchNativeLands(coordinates.value),
    ];
    address.value = await requests[0];
    loading.value = false;
    const [_, countySubdivisionResponse, zoneResponse] = await Promise.all(requests);
    countySubdivision.value = countySubdivisionResponse?.data?.value;
    zone.value = zoneResponse?.data?.value;
    // nativeLands.value = nativeLandsResponse?.data?.value;
  } catch (error) {
    console.error(error);
  }
  loading.value = false;
}

// A location in the URL is looked up in setup, so the server renders it. County subdivision and zone are optional
// (the zone is US only), so their failures leave them empty, as in update().
const initialData = useInitialLocationData("address-page", async (location) => {
  const [addressResponse, countySubdivisionResponse, zoneResponse] = await Promise.all([
    reverseGeocode(location),
    fetchCountySubdivision(location).catch(() => null),
    fetchZone(location).catch(() => null),
  ]);
  return { address: addressResponse, countySubdivision: countySubdivisionResponse, zone: zoneResponse };
});

// update content when selected location changes (and for the initial location, unless it loaded above)
onCoordinatesChange(update);

watchEffect(() => {
  watchMode.value ? startWatching() : stopWatching();
});

// load page content
const page = await useContent(`/address/${props.path}`);

if (initialData) {
  await initialData;
  // on failure, keep loading: the browser looks it up again once mounted
  if (initialData.data.value) {
    address.value = initialData.data.value.address;
    countySubdivision.value = initialData.data.value.countySubdivision;
    zone.value = initialData.data.value.zone;
    loading.value = false;
  }
}

const fieldValue = computed(() => {
  return getDataValue(page.value.meta.field);
});

// "your city", "your ZIP code", "your GPS coordinates"
const locationSubject = computed(() => {
  const words = page.value.label.split(" ").map((word) => (word === word.toUpperCase() ? word : word.toLowerCase()));
  return `your ${words.join(" ")}`;
});

// there is nothing to load until a location is chosen
watch(locationError, (error) => {
  if (error) {
    loading.value = false;
  }
});

useSeoMeta({
  title: () => {
    if (placeName.value) {
      return `${placeName.value} - ${page.value.title}`;
    }
    return page.value.title;
  },
  titleTemplate: props.path == "address" ? "%s - What is my address?" : "%s - Where am I?",
  description: () => page.value.description,
  ogDescription: () => page.value.description,
});

defineOgImage({
  title: page.value.title,
  subtitle: "Learn more about your location",
});
</script>
